import { faXmark } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Modal } from '@mantine/core'
import {
  AWC_Y_TICKS,
  getAwcChartDomain,
  RootZoneSparkline,
} from 'App/dashboards/_components/RootZoneSparkline'
import { SoilChartExport } from 'App/dashboards/_components/SoilChartExport'
import {
  getSoilDeficitChipColors,
  SOIL_DEFICIT_CHIP_RADIUS,
} from 'App/dashboards/_components/SoilDeficitChip'
import { downloadSvgAsJpg } from 'App/dashboards/_utils/dashboardDownloads'
import { getRootZoneAverage } from 'App/dashboards/_utils/getRootZoneAverage'
import {
  soilDeficitZoneColor,
  soilDeficitZoneLabel,
  type TSoilDeficitZone,
} from 'App/dashboards/_utils/soilDeficit'
import type { TWaterMonitoringProbe } from 'App/dashboards/_utils/useWaterMonitoring'
import { translate } from 'i18n/i18n'
import moment from 'moment-timezone'
import { useCallback, useRef } from 'react'
import { colors, dashboardColors } from 'settings/colors'

const ZONES: TSoilDeficitZone[] = ['extremeDeficit', 'highDeficit', 'moderateDeficit', 'ideal', 'saturated']
// Plot geometry from the design: a 208px-tall plot, a 32px y-axis gutter and 8px between them. The
// x-axis labels are inset by the gutter + gap so they line up under the plot rather than the axis.
const PLOT_HEIGHT = 208
const Y_AXIS_WIDTH = 32
const AXIS_GAP = 8
// Half the 16px label line-height, so a label centres on its gridline instead of hanging below it.
const LABEL_HALF_LINE = 8
// The design's dialog is 664 wide; the plot is what's left after the dialog padding and the y-axis.
const DIALOG_WIDTH = 664
const DIALOG_PADDING_X = 24
const PLOT_WIDTH = DIALOG_WIDTH - DIALOG_PADDING_X * 2 - Y_AXIS_WIDTH - AXIS_GAP

const buttonBaseCss = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 8,
  height: 36,
  padding: '8px 16px',
  borderRadius: 8,
  cursor: 'pointer',
  fontSize: 14,
  lineHeight: '20px',
  fontWeight: 500,
  boxShadow: '0px 1px 1px rgba(0, 0, 0, 0.05)',
}

// Expanded view of a probe's Root Zone Avg: the same banded AWC chart as the table sparkline, drawn
// large with its own axes, a deficit-zone legend, and a JPEG export. (A richer interactive chart —
// hourly line, water events, hover tooltips — is the Map's Highcharts AWC chart; still deferred.)
export const SoilChartModal = ({
  probe,
  onClose,
}: {
  probe: TWaterMonitoringProbe | null
  onClose: () => void
}) => {
  // The export target is a separate, off-screen SVG carrying the whole dialog body — title, badge,
  // subtitle, axes, plot and legend. Rasterising the visible markup would capture only the plot,
  // since everything around it is DOM rather than SVG.
  const exportRef = useRef<SVGSVGElement>(null)

  const handleDownload = useCallback(() => {
    if (exportRef.current && probe) downloadSvgAsJpg(exportRef.current, `${probe.name}.jpg`)
  }, [probe])

  // Kept mounted while closed so Mantine can play its exit transition rather than snapping away.
  if (!probe) return <Modal opened={false} onClose={onClose} />

  const { yMin, yMax } = getAwcChartDomain(probe.rootZoneSeries)
  const span = yMax - yMin || 1
  // Same mapping the SVG uses, so a label sits exactly on the band boundary it names.
  const topOf = (tick: number): number => PLOT_HEIGHT * (1 - (tick - yMin) / span) - LABEL_HALF_LINE
  const visibleYTicks = AWC_Y_TICKS.filter((tick) => tick >= yMin && tick <= yMax)
  const rootZoneAverage = getRootZoneAverage(probe.rootZoneSeries)
  const reading = rootZoneAverage === null ? null : `${Math.round(rootZoneAverage * 10) / 10}%`

  const readingColors =
    rootZoneAverage === null ? null : getSoilDeficitChipColors(rootZoneAverage, probe.madValue ?? undefined)

  const timezone = moment.tz.guess()

  const formatDate = (iso: string | undefined): string =>
    iso ? translate.dates.format(moment.tz(iso, timezone), 'MMM D') : ''

  const dates = probe.rootZoneDates ?? []
  const firstDate = formatDate(dates[0])
  const lastDate = dates.length > 1 ? formatDate(dates[dates.length - 1]) : ''

  // Four evenly-spaced ticks across the window, as drawn — first, last and two between.
  const xTicks = dates.length
    ? [0, 1, 2, 3].map((i) => formatDate(dates[Math.round((i / 3) * (dates.length - 1))]))
    : []

  const rangeLabel = [firstDate, lastDate].filter(Boolean).join(' – ')

  const subtitle = [
    translate.phrases.banyanApp('Root Zone Avg'),
    translate.phrases.banyanApp('Past 7 Days'),
    rangeLabel,
  ]
    .filter(Boolean)
    .join(' · ')

  return (
    <Modal
      opened
      onClose={onClose}
      centered
      withCloseButton={false}
      padding={0}
      size={DIALOG_WIDTH}
      styles={{
        content: {
          border: `1px solid ${dashboardColors.border}`,
          borderRadius: 10,
          overflow: 'hidden',
          boxShadow: '0px 10px 15px -3px rgba(0, 0, 0, 0.1), 0px 4px 6px -4px rgba(0, 0, 0, 0.1)',
        },
        body: { padding: 0 },
      }}
    >
      <div css={{ display: 'flex', flexDirection: 'column', background: colors.white }}>
        <div
          css={{
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
            padding: `24px ${DIALOG_PADDING_X}px 16px`,
          }}
        >
          <div css={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div css={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span
                css={{
                  fontSize: 16,
                  lineHeight: '20px',
                  fontWeight: 500,
                  color: dashboardColors.foreground,
                }}
              >
                {probe.name}
              </span>
              {reading !== null && (
                <span
                  css={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: '2px 8px',
                    // A soil-moisture reading like the table's Current chip, so it takes the same shape.
                    borderRadius: SOIL_DEFICIT_CHIP_RADIUS,
                    // Coloured by deficit band on the same scale as the Current chip, so a colour means
                    // the same thing in the table and the chart.
                    background: readingColors?.background ?? dashboardColors.primary,
                    color: readingColors?.color ?? dashboardColors.primaryForeground,
                    fontSize: 12,
                    lineHeight: '16px',
                    fontWeight: 500,
                  }}
                >
                  {reading}
                </span>
              )}
            </div>
            <button
              aria-label={translate.phrases.banyanApp('Close')}
              onClick={onClose}
              css={{
                display: 'inline-flex',
                width: 16,
                height: 16,
                padding: 0,
                border: 'none',
                background: 'transparent',
                opacity: 0.7,
                cursor: 'pointer',
                color: dashboardColors.foreground,
              }}
            >
              <FontAwesomeIcon icon={faXmark} css={{ fontSize: 16 }} />
            </button>
          </div>
          <span css={{ fontSize: 14, lineHeight: '20px', color: dashboardColors.mutedText }}>{subtitle}</span>
        </div>

        <div
          css={{
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
            padding: `0 ${DIALOG_PADDING_X}px 16px`,
          }}
        >
          <div css={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div css={{ display: 'flex', alignItems: 'flex-start', gap: AXIS_GAP }}>
              <div
                css={{
                  position: 'relative',
                  width: Y_AXIS_WIDTH,
                  height: PLOT_HEIGHT,
                  fontSize: 12,
                  lineHeight: '16px',
                  textAlign: 'right',
                  color: dashboardColors.mutedText,
                }}
              >
                {visibleYTicks.map((tick) => (
                  <span
                    key={tick}
                    css={{ position: 'absolute', right: 0, width: Y_AXIS_WIDTH, top: topOf(tick) }}
                  >
                    {`${tick}%`}
                  </span>
                ))}
              </div>
              <div css={{ flex: 1, height: PLOT_HEIGHT }}>
                <RootZoneSparkline
                  values={probe.rootZoneSeries}
                  dates={probe.rootZoneDates}
                  madValue={probe.madValue ?? undefined}
                  width={PLOT_WIDTH}
                  height={PLOT_HEIGHT}
                />
              </div>
            </div>
            <div
              css={{
                display: 'flex',
                justifyContent: 'space-between',
                paddingLeft: Y_AXIS_WIDTH + AXIS_GAP,
                fontSize: 12,
                lineHeight: '16px',
                color: dashboardColors.mutedText,
                whiteSpace: 'nowrap',
              }}
            >
              {xTicks.map((label, index) => (
                <span key={`${label}-${index}`}>{label}</span>
              ))}
            </div>
          </div>

          <div css={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
            {ZONES.map((zone) => (
              <span
                key={zone}
                css={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  fontSize: 12,
                  lineHeight: '16px',
                  color: dashboardColors.foreground,
                }}
              >
                <span
                  css={{
                    width: 10,
                    height: 10,
                    borderRadius: 2,
                    background: soilDeficitZoneColor[zone],
                  }}
                />
                {soilDeficitZoneLabel(zone)}
              </span>
            ))}
          </div>
        </div>

        {/* Off-screen rather than display:none — a collapsed element has no layout for
            downloadSvgAsJpg to measure, and it would export at zero size. */}
        <div css={{ position: 'absolute', left: -99999, top: 0, pointerEvents: 'none' }} aria-hidden>
          <SoilChartExport
            svgRef={exportRef}
            width={DIALOG_WIDTH}
            title={probe.name}
            reading={reading}
            readingColors={readingColors}
            subtitle={subtitle}
            values={probe.rootZoneSeries}
            dates={probe.rootZoneDates}
            madValue={probe.madValue ?? undefined}
            yTicks={visibleYTicks}
            topOf={topOf}
            xTicks={xTicks}
            zones={ZONES}
          />
        </div>

        <div
          css={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 8,
            padding: `16px ${DIALOG_PADDING_X}px`,
            borderTop: `1px solid ${dashboardColors.border}`,
            background: dashboardColors.accent,
          }}
        >
          <button
            onClick={handleDownload}
            css={{
              ...buttonBaseCss,
              background: colors.white,
              border: `1px solid ${dashboardColors.border}`,
              color: dashboardColors.foreground,
            }}
          >
            {translate.phrases.banyanApp('Download JPEG')}
          </button>
          <button
            onClick={onClose}
            css={{
              ...buttonBaseCss,
              background: dashboardColors.primary,
              border: 'none',
              color: dashboardColors.primaryForeground,
            }}
          >
            {translate.phrases.banyanApp('Close')}
          </button>
        </div>
      </div>
    </Modal>
  )
}
